import React, { useState, useEffect, useRef } from 'react';
import { Plus, Edit2, Trash2, Search, X, Save, Ban, Copy, FileText, AlertCircle, Printer } from 'lucide-react';
import '../styles/VoucherForms.css';
import apiClient from '../services/api';

const JournalEntry = () => {
  // Session management
  const getSessionData = () => {
    try {
      const sessionStr = localStorage.getItem('ltms_session');
      if (!sessionStr) return null;
      const sessionData = JSON.parse(sessionStr);
      return sessionData?.session || sessionData;
    } catch (error) {
      console.error('Error parsing session data:', error);
      return null;
    }
  };

  const session = getSessionData();
  const companyId = session?.companyid;
  const yearId = session?.yearid;
  const printRef = useRef();

  // State management
  const [vouchers, setVouchers] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  
  // Form data
  const [formData, setFormData] = useState({
    acjournalid: null,
    companyid: companyId || null,
    fyearid: yearId || null,
    voucherno: '',
    transdate: new Date().toISOString().split('T')[0],
    vouchertype: 'JV',
    narration: '',
    details: []
  });

  // Detail line being edited
  const [currentDetail, setCurrentDetail] = useState({
    acheadid: '',
    debitAmount: '',
    creditAmount: '',
    narration: ''
  });
  
  const [errors, setErrors] = useState({});
  const [submitLoading, setSubmitLoading] = useState(false);

  useEffect(() => {
    if (!companyId || !yearId) {
      setLoading(false);
      return;
    }
    fetchInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, yearId]);

  const fetchInitialData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchAccounts(),
        fetchVouchers()
      ]);
    } catch (error) {
      console.error('Error fetching initial data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAccounts = async () => {
    try {
      const response = await apiClient.get('/Acheads.php', {
        params: {
          companyid: companyId
        }
      });
      
      if (response.data.success) {
        setAccounts(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching accounts:', error);
      if (error.response?.status === 401) {
        alert('Session expired. Please login again.');
      }
    }
  };

  const fetchVouchers = async () => {
    try {
      const response = await apiClient.get('/Vouchers.php', {
        params: {
          companyid: companyId,
          fyearid: yearId,
          vouchertype: 'JV'
        }
      });
      
      if (response.data.success) {
        setVouchers(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching vouchers:', error);
      if (error.response?.status === 401) {
        alert('Session expired. Please login again.');
      }
    }
  };

  const getNextVoucherNumber = async () => {
    try {
      const response = await apiClient.get('/Vouchers.php', {
        params: {
          nextvoucher: true,
          companyid: companyId,
          fyearid: yearId,
          vouchertype: 'JV'
        }
      });
      
      if (response.data.success) {
        setFormData(prev => ({
          ...prev,
          voucherno: response.data.data.voucherno
        }));
      }
    } catch (error) {
      console.error('Error getting next voucher number:', error);
    }
  };

  const handleNew = async () => {
    resetForm();
    await getNextVoucherNumber();
    setEditMode(false);
    setShowModal(true);
  };

  const handleEdit = async (voucher) => {
    try {
      const response = await apiClient.get(`/Vouchers.php`, {
        params: { id: voucher.acjournalid }
      });
      
      if (response.data.success) {
        const { master, details } = response.data.data;
        
        const formattedDetails = details.map(d => ({
          acheadid: d.acheadid,
          acheadname: d.acheadname,
          acheadcode: d.acheadcode,
          debitAmount: d.amount > 0 ? d.amount : 0,
          creditAmount: d.amount < 0 ? Math.abs(d.amount) : 0,
          narration: d.remarks || ''
        }));
        
        setFormData({
          acjournalid: master.acjournalid,
          companyid: master.companyid,
          fyearid: master.fyearid,
          voucherno: master.voucherno,
          transdate: master.transdate,
          vouchertype: master.vouchertype,
          narration: master.remarks || '',
          details: formattedDetails
        });
        
        setEditMode(true);
        setShowModal(true);
      }
    } catch (error) {
      console.error('Error loading voucher:', error);
      alert('Error loading voucher details');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this journal entry?\n\nThis action cannot be undone.')) {
      return;
    }
    
    try {
      const response = await apiClient.delete('/Vouchers.php', {
        data: { acjournalid: id }
      });
      
      if (response.data.success) {
        alert('Journal entry deleted successfully!');
        fetchVouchers();
      } else {
        alert('Error: ' + response.data.message);
      }
    } catch (error) {
      console.error('Error deleting voucher:', error);
      if (error.response?.status === 401) {
        alert('Session expired. Please login again.');
      } else {
        alert('Error deleting voucher. Please try again.');
      }
    }
  };

  const handleDuplicate = async (voucher) => {
    try {
      const response = await apiClient.get(`/Vouchers.php`, {
        params: { id: voucher.acjournalid }
      });
      
      if (response.data.success) {
        const { master, details } = response.data.data;
        
        await getNextVoucherNumber();
        
        const formattedDetails = details.map(d => ({
          acheadid: d.acheadid,
          acheadname: d.acheadname,
          acheadcode: d.acheadcode,
          debitAmount: d.amount > 0 ? d.amount : 0,
          creditAmount: d.amount < 0 ? Math.abs(d.amount) : 0,
          narration: d.remarks || ''
        }));
        
        setFormData(prev => ({
          ...prev,
          acjournalid: null,
          transdate: new Date().toISOString().split('T')[0],
          narration: master.remarks || '',
          details: formattedDetails
        }));
        
        setEditMode(false);
        setShowModal(true);
      }
    } catch (error) {
      console.error('Error duplicating voucher:', error);
      alert('Error duplicating voucher');
    }
  };

  const handlePrint = () => {
    if (formData.details.length === 0) {
      alert('Please add transaction details before printing');
      return;
    }

    if (!isBalanced()) {
      alert('Journal entry must be balanced before printing');
      return;
    }

    const printWindow = window.open('', '', 'height=600,width=800');
    
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Journal Entry - ${formData.voucherno}</title>
        <style>
          * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
          }
          
          body {
            font-family: Arial, sans-serif;
            padding: 20mm;
            background: white;
          }
          
          .print-header {
            text-align: center;
            margin-bottom: 30px;
            border-bottom: 3px solid #000;
            padding-bottom: 20px;
          }
          
          .company-name {
            font-size: 24px;
            font-weight: bold;
            margin-bottom: 5px;
          }
          
          .voucher-title {
            font-size: 18px;
            font-weight: bold;
            margin-top: 10px;
          }
          
          .voucher-info {
            display: flex;
            justify-content: space-between;
            margin: 20px 0;
            padding: 15px;
            background: #f5f5f5;
            border: 1px solid #ddd;
          }
          
          .info-item {
            display: flex;
            flex-direction: column;
          }
          
          .info-label {
            font-size: 11px;
            color: #666;
            text-transform: uppercase;
            margin-bottom: 4px;
          }
          
          .info-value {
            font-size: 14px;
            font-weight: bold;
            font-family: 'Courier New', monospace;
          }
          
          .details-table {
            width: 100%;
            border-collapse: collapse;
            margin: 20px 0;
          }
          
          .details-table th {
            background: #000;
            color: white;
            padding: 12px 8px;
            text-align: left;
            font-size: 12px;
            text-transform: uppercase;
            font-weight: bold;
          }
          
          .details-table td {
            padding: 10px 8px;
            border-bottom: 1px solid #ddd;
            font-size: 13px;
          }
          
          .details-table tbody tr:hover {
            background: #f9f9f9;
          }
          
          .details-table .serial {
            text-align: center;
            width: 40px;
          }
          
          .details-table .account-name {
            font-weight: 600;
          }
          
          .details-table .account-code {
            font-size: 11px;
            color: #666;
            display: block;
          }
          
          .details-table .amount {
            text-align: right;
            font-family: 'Courier New', monospace;
            font-weight: bold;
          }
          
          .details-table .debit {
            color: #dc2626;
          }
          
          .details-table .credit {
            color: #059669;
          }
          
          .details-table .narration {
            font-style: italic;
            color: #666;
          }
          
          .totals-section {
            margin: 20px 0;
            border-top: 3px double #000;
            padding-top: 15px;
          }
          
          .total-row {
            display: flex;
            justify-content: space-between;
            padding: 8px 0;
            font-size: 14px;
          }
          
          .total-row.grand-total {
            font-size: 16px;
            font-weight: bold;
            background: #f5f5f5;
            padding: 12px;
            margin-top: 10px;
            border: 2px solid #000;
          }
          
          .total-label {
            font-weight: bold;
          }
          
          .total-value {
            font-family: 'Courier New', monospace;
            font-weight: bold;
          }
          
          .narration-section {
            margin: 20px 0;
            padding: 15px;
            background: #f9f9f9;
            border-left: 4px solid #667eea;
          }
          
          .narration-label {
            font-size: 11px;
            color: #666;
            text-transform: uppercase;
            margin-bottom: 8px;
            font-weight: bold;
          }
          
          .narration-text {
            font-size: 13px;
            line-height: 1.6;
          }
          
          .signatures {
            display: flex;
            justify-content: space-between;
            margin-top: 60px;
            padding-top: 20px;
            border-top: 1px solid #ddd;
          }
          
          .signature-box {
            text-align: center;
            width: 200px;
          }
          
          .signature-line {
            border-top: 2px solid #000;
            margin-top: 60px;
            margin-bottom: 8px;
          }
          
          .signature-label {
            font-size: 12px;
            color: #666;
          }
          
          .footer {
            margin-top: 40px;
            text-align: center;
            font-size: 11px;
            color: #999;
            border-top: 1px solid #ddd;
            padding-top: 15px;
          }
          
          .balanced-badge {
            display: inline-block;
            background: #10b981;
            color: white;
            padding: 4px 12px;
            border-radius: 4px;
            font-size: 12px;
            font-weight: bold;
          }
          
          @media print {
            body {
              padding: 0;
            }
            
            .print-header {
              page-break-after: avoid;
            }
            
            .details-table {
              page-break-inside: avoid;
            }
          }
        </style>
      </head>
      <body>
        <div class="print-header">
          <div class="company-name">${session?.companyname || 'Company Name'}</div>
          <div class="voucher-title">JOURNAL VOUCHER</div>
        </div>
        
        <div class="voucher-info">
          <div class="info-item">
            <span class="info-label">Voucher No</span>
            <span class="info-value">${formData.voucherno}</span>
          </div>
          <div class="info-item">
            <span class="info-label">Date</span>
            <span class="info-value">${new Date(formData.transdate).toLocaleDateString('en-GB')}</span>
          </div>
          <div class="info-item">
            <span class="info-label">Type</span>
            <span class="info-value">JV</span>
          </div>
          <div class="info-item">
            <span class="info-label">Status</span>
            <span class="balanced-badge">✓ BALANCED</span>
          </div>
        </div>
        
        <table class="details-table">
          <thead>
            <tr>
              <th class="serial">#</th>
              <th>Account Name</th>
              <th style="width: 120px; text-align: right;">Debit (Dr)</th>
              <th style="width: 120px; text-align: right;">Credit (Cr)</th>
              <th style="width: 250px;">Narration</th>
            </tr>
          </thead>
          <tbody>
            ${formData.details.map((detail, index) => `
              <tr>
                <td class="serial">${index + 1}</td>
                <td>
                  <span class="account-name">${detail.acheadname}</span>
                  ${detail.acheadcode ? `<span class="account-code">${detail.acheadcode}</span>` : ''}
                </td>
                <td class="amount debit">
                  ${detail.debitAmount > 0 ? `₹ ${parseFloat(detail.debitAmount).toLocaleString('en-IN', {minimumFractionDigits: 2})}` : '-'}
                </td>
                <td class="amount credit">
                  ${detail.creditAmount > 0 ? `₹ ${parseFloat(detail.creditAmount).toLocaleString('en-IN', {minimumFractionDigits: 2})}` : '-'}
                </td>
                <td class="narration">${detail.narration || '-'}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
        
        <div class="totals-section">
          <div class="total-row">
            <span class="total-label">Total Debit:</span>
            <span class="total-value debit">₹ ${calculateDebitTotal().toLocaleString('en-IN', {minimumFractionDigits: 2})}</span>
          </div>
          <div class="total-row">
            <span class="total-label">Total Credit:</span>
            <span class="total-value credit">₹ ${calculateCreditTotal().toLocaleString('en-IN', {minimumFractionDigits: 2})}</span>
          </div>
          <div class="total-row grand-total">
            <span class="total-label">Difference:</span>
            <span class="total-value">₹ ${Math.abs(getDifference()).toLocaleString('en-IN', {minimumFractionDigits: 2})} ✓</span>
          </div>
        </div>
        
        ${formData.narration ? `
          <div class="narration-section">
            <div class="narration-label">Narration</div>
            <div class="narration-text">${formData.narration}</div>
          </div>
        ` : ''}
        
        <div class="signatures">
          <div class="signature-box">
            <div class="signature-line"></div>
            <div class="signature-label">Prepared By</div>
          </div>
          <div class="signature-box">
            <div class="signature-line"></div>
            <div class="signature-label">Verified By</div>
          </div>
          <div class="signature-box">
            <div class="signature-line"></div>
            <div class="signature-label">Approved By</div>
          </div>
        </div>
        
        <div class="footer">
          <p>This is a computer-generated document. No signature is required.</p>
          <p>Printed on: ${new Date().toLocaleString('en-GB')}</p>
        </div>
      </body>
      </html>
    `);
    
    printWindow.document.close();
    printWindow.focus();
    
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 250);
  };

  const addDetailLine = () => {
    if (!currentDetail.acheadid) {
      alert('Please select an account');
      return;
    }
    
    const hasDebit = currentDetail.debitAmount && parseFloat(currentDetail.debitAmount) > 0;
    const hasCredit = currentDetail.creditAmount && parseFloat(currentDetail.creditAmount) > 0;
    
    if (!hasDebit && !hasCredit) {
      alert('Please enter either debit or credit amount');
      return;
    }
    
    if (hasDebit && hasCredit) {
      alert('Cannot enter both debit and credit for the same line');
      return;
    }
    
    const account = accounts.find(a => a.acheadid === parseInt(currentDetail.acheadid));
    if (!account) {
      alert('Invalid account selected');
      return;
    }
    
    const newDetail = {
      acheadid: parseInt(currentDetail.acheadid),
      acheadname: account.acheadname,
      acheadcode: account.acheadcode || '',
      debitAmount: hasDebit ? parseFloat(currentDetail.debitAmount) : 0,
      creditAmount: hasCredit ? parseFloat(currentDetail.creditAmount) : 0,
      narration: currentDetail.narration
    };
    
    setFormData(prev => ({
      ...prev,
      details: [...prev.details, newDetail]
    }));
    
    setCurrentDetail({
      acheadid: '',
      debitAmount: '',
      creditAmount: '',
      narration: ''
    });
  };

  const removeDetailLine = (index) => {
    setFormData(prev => ({
      ...prev,
      details: prev.details.filter((_, i) => i !== index)
    }));
  };

  const calculateDebitTotal = () => {
    return formData.details.reduce((sum, detail) => sum + parseFloat(detail.debitAmount || 0), 0);
  };

  const calculateCreditTotal = () => {
    return formData.details.reduce((sum, detail) => sum + parseFloat(detail.creditAmount || 0), 0);
  };

  const getDifference = () => {
    return calculateDebitTotal() - calculateCreditTotal();
  };

  const isBalanced = () => {
    return Math.abs(getDifference()) < 0.01;
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.voucherno.trim()) {
      newErrors.voucherno = 'Voucher number is required';
    }
    
    if (!formData.transdate) {
      newErrors.transdate = 'Transaction date is required';
    }
    
    if (formData.details.length === 0) {
      newErrors.details = 'At least one detail line is required';
    }
    
    if (formData.details.length === 1) {
      newErrors.details = 'Journal entry requires at least 2 lines (debit and credit)';
    }
    
    if (!isBalanced()) {
      newErrors.balance = 'Journal entry must be balanced (Total Debit = Total Credit)';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setSubmitLoading(true);
    
    try {
      const voucherDetails = formData.details.map(d => ({
        acheadid: d.acheadid,
        amount: d.debitAmount > 0 ? d.debitAmount : -d.creditAmount,
        remarks: d.narration || ''
      }));
      
      const payload = {
        companyid: companyId,
        fyearid: yearId,
        voucherno: formData.voucherno,
        transdate: formData.transdate,
        vouchertype: formData.vouchertype,
        remarks: formData.narration,
        details: voucherDetails
      };
      
      if (editMode) {
        payload.acjournalid = formData.acjournalid;
      }
      
      const method = editMode ? 'put' : 'post';
      const response = await apiClient[method]('/Vouchers.php', payload);
      
      if (response.data.success) {
        alert(editMode ? 'Journal entry updated successfully!' : 'Journal entry created successfully!');
        setShowModal(false);
        resetForm();
        fetchVouchers();
      } else {
        alert('Error: ' + response.data.message);
      }
    } catch (error) {
      console.error('Error saving journal entry:', error);
      if (error.response?.status === 401) {
        alert('Session expired. Please login again.');
      } else {
        alert('Error saving journal entry. Please try again.');
      }
    } finally {
      setSubmitLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      acjournalid: null,
      companyid: companyId || null,
      fyearid: yearId || null,
      voucherno: '',
      transdate: new Date().toISOString().split('T')[0],
      vouchertype: 'JV',
      narration: '',
      details: []
    });
    setCurrentDetail({
      acheadid: '',
      debitAmount: '',
      creditAmount: '',
      narration: ''
    });
    setErrors({});
  };

  const closeModal = () => {
    if (formData.details.length > 0) {
      if (!window.confirm('You have unsaved changes. Are you sure you want to close?')) {
        return;
      }
    }
    setShowModal(false);
    resetForm();
  };

  if (!companyId || !yearId) {
    return (
      <div className="voucher-container">
        <div className="empty-state">
          <h3>No Session Context</h3>
          <p>Please select a company and financial year from the dashboard.</p>
        </div>
      </div>
    );
  }

  const filteredVouchers = vouchers.filter(v =>
    v.voucherno?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    v.remarks?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="voucher-container">
      <div className="voucher-header">
        <div className="header-left">
          <h2>📒 Journal Voucher Entry</h2>
          <p className="subtitle">Record general journal entries with proper double-entry accounting</p>
        </div>
        <button className="btn-primary" onClick={handleNew}>
          <Plus size={20} />
          New Journal Entry
        </button>
      </div>

      <div className="voucher-toolbar">
        <div className="search-box">
          <Search size={20} />
          <input
            type="text"
            placeholder="Search by voucher number or narration..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <button className="btn-clear" onClick={() => setSearchTerm('')}>
              <X size={16} />
            </button>
          )}
        </div>
        <div className="toolbar-info">
          <strong>{filteredVouchers.length}</strong> {filteredVouchers.length === 1 ? 'entry' : 'entries'}
        </div>
      </div>

      {loading ? (
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading journal entries...</p>
        </div>
      ) : filteredVouchers.length === 0 ? (
        <div className="empty-state">
          <FileText size={48} color="#9ca3af" />
          <h3>No Journal Entries Found</h3>
          <p>{searchTerm ? 'No entries match your search' : 'Click "New Journal Entry" to create your first entry'}</p>
          {searchTerm && <button className="btn-secondary" onClick={() => setSearchTerm('')}>Clear Search</button>}
        </div>
      ) : (
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Voucher No</th>
                <th>Date</th>
                <th>Debit Amount</th>
                <th>Credit Amount</th>
                <th>Narration</th>
                <th className="actions-column">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredVouchers.map((voucher) => (
                <tr key={voucher.acjournalid}>
                  <td className="font-mono font-semibold">{voucher.voucherno}</td>
                  <td>{new Date(voucher.transdate).toLocaleDateString('en-GB')}</td>
                  <td className="amount-cell debit-amount">
                    ₹ {parseFloat(voucher.total_amount || 0).toLocaleString('en-IN', {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                  </td>
                  <td className="amount-cell credit-amount">
                    ₹ {parseFloat(voucher.total_amount || 0).toLocaleString('en-IN', {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                  </td>
                  <td className="narration-cell">{voucher.remarks || '-'}</td>
                  <td className="actions-column">
                    <button
                      className="btn-icon btn-edit"
                      onClick={() => handleEdit(voucher)}
                      title="Edit"
                    >
                      <Edit2 size={16} />
                    </button>
                    <button
                      className="btn-icon btn-copy"
                      onClick={() => handleDuplicate(voucher)}
                      title="Duplicate"
                    >
                      <Copy size={16} />
                    </button>
                    <button
                      className="btn-icon btn-delete"
                      onClick={() => handleDelete(voucher.acjournalid)}
                      title="Delete"
                    >
                      <Trash2 size={16} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content modal-xlarge voucher-modal journal-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div>
                <h3>{editMode ? '✏️ Edit' : '➕ New'} Journal Entry</h3>
                <p className="modal-subtitle">Double-entry bookkeeping system</p>
              </div>
              <button className="btn-close" onClick={closeModal} title="Close (Esc)">
                <X size={24} />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="voucher-form">
              {/* Voucher Header */}
              <div className="form-section">
                <div className="section-header">
                  <h4>📋 Voucher Details</h4>
                </div>
                <div className="form-row form-row-3">
                  <div className="form-group">
                    <label htmlFor="voucherno">
                      Voucher No <span className="required">*</span>
                      <span className="auto-badge">Auto</span>
                    </label>
                    <div className="voucher-no-wrapper">
                      <input
                        type="text"
                        id="voucherno"
                        value={formData.voucherno}
                        readOnly
                        className="voucher-no-locked"
                        placeholder="Auto-generating..."
                      />
                      <span className="lock-icon" title="Auto-generated">🔒</span>
                    </div>
                    {errors.voucherno && <span className="error-message">{errors.voucherno}</span>}
                  </div>

                  <div className="form-group">
                    <label htmlFor="transdate">
                      Date <span className="required">*</span>
                    </label>
                    <input
                      type="date"
                      id="transdate"
                      value={formData.transdate}
                      onChange={(e) => setFormData({ ...formData, transdate: e.target.value })}
                      className={errors.transdate ? 'error' : ''}
                    />
                    {errors.transdate && <span className="error-message">{errors.transdate}</span>}
                  </div>

                  <div className="form-group">
                    <label htmlFor="vouchertype">Type</label>
                    <input
                      type="text"
                      id="vouchertype"
                      value="Journal Voucher (JV)"
                      disabled
                      style={{ background: '#f3f4f6', cursor: 'not-allowed', fontWeight: '600' }}
                    />
                  </div>
                </div>
              </div>

              {/* Detail Lines Entry */}
              <div className="form-section">
                <div className="section-header">
                  <h4>💳 Transaction Details <span className="required">*</span></h4>
                  {errors.details && (
                    <span className="error-badge">
                      <AlertCircle size={14} /> {errors.details}
                    </span>
                  )}
                </div>
                
                {/* Add Detail Line Form */}
                <div className="detail-entry-card">
                  <div className="detail-input-grid">
                    <div className="form-group" style={{ gridColumn: 'span 2' }}>
                      <label>Account Ledger <span className="required">*</span></label>
                      <select
                        value={currentDetail.acheadid}
                        onChange={(e) => setCurrentDetail({ ...currentDetail, acheadid: e.target.value })}
                        className="account-select"
                      >
                        <option value="">-- Select Account Ledger --</option>
                        {accounts.map((account) => (
                          <option key={account.acheadid} value={account.acheadid}>
                            {account.acheadcode ? `${account.acheadcode} - ` : ''}{account.acheadname}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="form-group">
                      <label>Debit Amount (Dr)</label>
                      <input
                        type="number"
                        value={currentDetail.debitAmount}
                        onChange={(e) => setCurrentDetail({ 
                          ...currentDetail, 
                          debitAmount: e.target.value,
                          creditAmount: ''
                        })}
                        placeholder="0.00"
                        step="0.01"
                        min="0"
                        className="amount-input debit-input"
                      />
                    </div>

                    <div className="form-group">
                      <label>Credit Amount (Cr)</label>
                      <input
                        type="number"
                        value={currentDetail.creditAmount}
                        onChange={(e) => setCurrentDetail({ 
                          ...currentDetail, 
                          creditAmount: e.target.value,
                          debitAmount: ''
                        })}
                        placeholder="0.00"
                        step="0.01"
                        min="0"
                        className="amount-input credit-input"
                      />
                    </div>

                    <div className="form-group" style={{ gridColumn: 'span 2' }}>
                      <label>Narration (Optional)</label>
                      <input
                        type="text"
                        value={currentDetail.narration}
                        onChange={(e) => setCurrentDetail({ ...currentDetail, narration: e.target.value })}
                        placeholder="Brief description of this transaction line"
                        maxLength="200"
                      />
                    </div>

                    <div style={{ gridColumn: 'span 3', display: 'flex', justifyContent: 'flex-end' }}>
                      <button
                        type="button"
                        className="btn-add-line"
                        onClick={addDetailLine}
                      >
                        <Plus size={18} />
                        Add Line
                      </button>
                    </div>
                  </div>
                </div>

                {/* Details Table */}
                {formData.details.length > 0 ? (
                  <div className="details-table journal-details-table">
                    <table>
                      <thead>
                        <tr>
                          <th style={{ width: '50px' }}>#</th>
                          <th>Account Name</th>
                          <th style={{ width: '150px' }}>Debit (Dr)</th>
                          <th style={{ width: '150px' }}>Credit (Cr)</th>
                          <th>Narration</th>
                          <th style={{ width: '60px' }}>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {formData.details.map((detail, index) => (
                          <tr key={index}>
                            <td className="text-center">{index + 1}</td>
                            <td>
                              <div className="account-cell">
                                <strong>{detail.acheadname}</strong>
                                {detail.acheadcode && <small>{detail.acheadcode}</small>}
                              </div>
                            </td>
                            <td className="amount-cell debit-cell">
                              {detail.debitAmount > 0 ? (
                                <span className="dr-amount">
                                  ₹ {parseFloat(detail.debitAmount).toLocaleString('en-IN', {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                                </span>
                              ) : (
                                <span className="no-amount">-</span>
                              )}
                            </td>
                            <td className="amount-cell credit-cell">
                              {detail.creditAmount > 0 ? (
                                <span className="cr-amount">
                                  ₹ {parseFloat(detail.creditAmount).toLocaleString('en-IN', {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                                </span>
                              ) : (
                                <span className="no-amount">-</span>
                              )}
                            </td>
                            <td className="narration-text">{detail.narration || '-'}</td>
                            <td className="text-center">
                              <button
                                type="button"
                                className="btn-icon btn-delete-small"
                                onClick={() => removeDetailLine(index)}
                                title="Remove line"
                              >
                                <Trash2 size={14} />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>

                    {/* Totals Summary */}
                    <div className={`journal-totals-summary ${!isBalanced() ? 'unbalanced' : ''}`}>
                      <div className="totals-grid">
                        <div className="total-box debit-total">
                          <span className="total-label">Total Debit (Dr)</span>
                          <span className="total-value">
                            ₹ {calculateDebitTotal().toLocaleString('en-IN', {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                          </span>
                        </div>

                        <div className="total-box credit-total">
                          <span className="total-label">Total Credit (Cr)</span>
                          <span className="total-value">
                            ₹ {calculateCreditTotal().toLocaleString('en-IN', {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                          </span>
                        </div>

                        <div className={`total-box difference-total ${isBalanced() ? 'balanced' : 'not-balanced'}`}>
                          <span className="total-label">Difference</span>
                          <span className="total-value">
                            {isBalanced() ? (
                              <>✅ Balanced</>
                            ) : (
                              <>
                                ⚠️ ₹ {Math.abs(getDifference()).toLocaleString('en-IN', {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                                <small>{getDifference() > 0 ? '(Dr excess)' : '(Cr excess)'}</small>
                              </>
                            )}
                          </span>
                        </div>
                      </div>
                      
                      {errors.balance && (
                        <div className="balance-error">
                          <AlertCircle size={16} />
                          <span>{errors.balance}</span>
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="empty-details-message">
                    <FileText size={32} color="#9ca3af" />
                    <p>No transaction lines added yet</p>
                    <small>Add at least 2 lines (one debit and one credit)</small>
                  </div>
                )}
              </div>

              {/* Master Narration */}
              <div className="form-section">
                <div className="section-header">
                  <h4>📝 Narration</h4>
                </div>
                <div className="form-group">
                  <label htmlFor="narration">Overall Narration/Description</label>
                  <textarea
                    id="narration"
                    value={formData.narration}
                    onChange={(e) => setFormData({ ...formData, narration: e.target.value })}
                    rows="3"
                    placeholder="Enter overall narration or description for this journal entry..."
                    maxLength="500"
                  />
                  <small className="char-count">{formData.narration.length}/500 characters</small>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="modal-footer">
                <div className="footer-info">
                  {formData.details.length > 0 && (
                    <span className="entry-count">
                      {formData.details.length} {formData.details.length === 1 ? 'line' : 'lines'} added
                    </span>
                  )}
                </div>
                <div className="footer-actions">
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={closeModal}
                    disabled={submitLoading}
                  >
                    <Ban size={16} />
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn-print"
                    onClick={handlePrint}
                    disabled={formData.details.length === 0 || !isBalanced()}
                    title="Print Voucher"
                  >
                    <Printer size={16} />
                    Print
                  </button>
                  <button
                    type="submit"
                    className="btn-primary"
                    disabled={submitLoading || formData.details.length < 2 || !isBalanced()}
                  >
                    <Save size={16} />
                    {submitLoading ? 'Saving...' : (editMode ? 'Update Entry' : 'Save Entry')}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default JournalEntry;